import * as THREE from 'three';
import { HEAD_COUNT } from '../lighting/stage-light-rig.js';

// Orchestrator registry entry (see visualizer-registry.js) - same reasoning
// as dust-layer.js's own PROFILE.

// Ces deux couches ne sont PAS rendues seules. adaptive-party.js les monte sur
// le rig de scene partage, le sol 60x60 et la camera de neon-pulse.js - son
// propre commentaire le dit : "same rig/floor/camera shape as neon-pulse.js's
// own setup". Ce qui arrive a l'ecran, ce sont donc les faisceaux de club de
// NeonPulse, avec ces particules dedans.
//
// Le PROFILE decrivait la COUCHE (poussiere lente, energie 0.20) et pas
// l'image. Les deux se contredisaient, et comme la fiche disait "calme",
// l'orchestrateur les choisissait justement pour du classique - d'ou des
// faisceaux de boite de nuit sur un nocturne, sans que NeonPulse ait jamais
// ete selectionne. Les valeurs ci-dessous decrivent ce qui est montre.
//
// Le `prefers` compte autant que les chiffres : sans lui, ces deux entrees
// n'avaient AUCUN axe de notation des que le bpm manquait (elles ne declaraient
// rien, contrairement aux visualiseurs calmes), tombaient a 0.5 comme tout le
// monde et ressortaient au hasard uniforme.
export const PROFILE = {
  id: 'storm',
  family: 'particles',
  energyLevel: 0.62,
  bpmMin: 95,
  bpmMax: 165,
  movement: 0.55,
  danceability: 0.70,
  tonality: 0.50,
  prefers: { dynamics: [0.0, 0.60], percussiveness: [0.45, 1.0] },
  enabled: true
};

// Dense dancefloor-crowd-density particle field that the rig sweeps
// *through* to reveal, rather than particles that light themselves up.
// Extracted from what used to be the whole of visualizers/particle-
// storm.js so it can also be swapped in as one of several rotating
// layers under NeonPulse's persistent rig/camera - see that file's
// layer-cycling comment.
//
// At this particle count (~2200), computing "how close is this particle
// to any of the 5 beam targets" in JS per-frame would be 11,000 distance
// checks/frame just for that - instead it's done on the GPU via a custom
// ShaderMaterial, with the beam target positions passed in as a uniform
// each frame. GLSL1 (ShaderMaterial's default), not GLSL3-only syntax,
// since some 2019-era Tizen sets may still be WebGL1.

const PARTICLE_COUNT = 1300; // trimmed from 1800 - real-TV perf headroom, still reads as dense
const FIELD_RADIUS = 9;
const BEAM_REVEAL_RADIUS = 2.3; // matches roughly where the rig's cones meet the floor
// Was 0.04 - at that level, particles the beams weren't currently
// sweeping over were essentially invisible, so only the small central
// area the rig's sweepSpread actually reaches ever looked populated even
// though the field itself spans the full FIELD_RADIUS - "particles only
// in the middle of the screen". Raised enough that the whole field reads
// as populated at a dim ambient level, with beam-revealed particles
// still popping clearly brighter on top.
const BASE_DIM = 0.16;
const POINT_WORLD_SIZE = 0.09; // base sprite diameter in world units
const POINT_WORLD_VARIANCE = 0.05;
// Beat-locked "breathe" (see beat-pulse.js) - the whole field grows
// slightly on the beat rather than flashing brighter, a better fit for a
// dense particle field than a brightness strobe would be.
const BEAT_PULSE_AMOUNT = 0.45;
const BEAT_PULSE_DECAY = 8;

const vertexShader = `
  uniform vec3 beamTargets[${HEAD_COUNT}];
  uniform float beamRadius;
  uniform float uTime;
  uniform float uSizeScale;
  attribute float aSeed;
  varying float vReveal;

  void main() {
    vec3 pos = position;
    pos.y += sin(uTime * 1.6 + aSeed * 6.2831) * 0.06;
    pos.x += sin(uTime * 0.8 + aSeed * 4.0) * 0.05;

    vec4 worldPosition = modelMatrix * vec4(pos, 1.0);

    float reveal = 0.0;
    for (int i = 0; i < ${HEAD_COUNT}; i++) {
      float d = distance(worldPosition.xz, beamTargets[i].xz);
      float r = 1.0 - smoothstep(0.0, beamRadius, d);
      reveal = max(reveal, r);
    }
    vReveal = reveal;

    vec4 mvPosition = viewMatrix * worldPosition;
    // Physically-correct world-size -> screen-pixels conversion (accounts
    // for perspective FOV and canvas height), not an arbitrary constant -
    // an unscaled magic number here previously produced sprites tens to
    // hundreds of pixels wide that merged into blobs under additive
    // blending.
    float worldSize = ${POINT_WORLD_SIZE.toFixed(3)} + aSeed * ${POINT_WORLD_VARIANCE.toFixed(3)};
    gl_PointSize = worldSize * uSizeScale / -mvPosition.z;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const fragmentShader = `
  uniform vec3 colorA;
  uniform vec3 colorB;
  uniform float uTime;
  uniform float uBaseDim;
  varying float vReveal;

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0; // 0 at center, 1 at sprite edge
    if (d > 1.0) discard;
    // Smooth radial falloff with no flat plateau - reads as a soft dot
    // rather than a hard-edged coin, so overlapping particles blend into
    // a gentle glow instead of stacking into solid discs.
    float edge = pow(1.0 - d, 2.0);

    float mixT = (sin(uTime * 0.2) + 1.0) / 2.0;
    vec3 color = mix(colorA, colorB, mixT);
    float brightness = uBaseDim + vReveal * 1.6;

    gl_FragColor = vec4(color * brightness, edge * (uBaseDim * 1.5 + vReveal));
  }
`;

const FALLBACK_A = new THREE.Color(0xff2fb0);
const FALLBACK_B = new THREE.Color(0x2fd8ff);

export class StormLayer {
  init(scene, camera) {
    this._camera = camera;

    const positions = new Float32Array(PARTICLE_COUNT * 3);
    const seeds = new Float32Array(PARTICLE_COUNT);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = Math.random() * Math.PI * 2;
      // sqrt for a uniform-density disc rather than center-clumped
      const radius = Math.sqrt(Math.random()) * FIELD_RADIUS;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = 0.1 + Math.random() * 1.7;
      positions[i * 3 + 2] = Math.sin(angle) * radius;
      seeds[i] = Math.random();
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));

    // Placeholder Vector3s until the first rig.update() call fills in
    // real target positions - keeps the uniform array always fully
    // populated (a GLSL array uniform can't be partially set).
    const beamTargets = [];
    for (let i = 0; i < HEAD_COUNT; i++) beamTargets.push(new THREE.Vector3());

    this._material = new THREE.ShaderMaterial({
      uniforms: {
        beamTargets: { value: beamTargets },
        beamRadius: { value: BEAM_REVEAL_RADIUS },
        uTime: { value: 0 },
        uBaseDim: { value: BASE_DIM },
        uSizeScale: { value: 1 },
        colorA: { value: FALLBACK_A.clone() },
        colorB: { value: FALLBACK_B.clone() }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    this._points = new THREE.Points(geometry, this._material);
    scene.add(this._points);

    this._time = 0;
  }

  update(deltaTime, profile, rig) {
    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;
    this._material.uniforms.uTime.value = this._time;

    // Recomputed every frame (a couple of scalar ops, not per-particle)
    // so it stays correct across window/canvas resizes without needing
    // SceneManager to plumb a resize hook into visualizers.
    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    const pulse = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
    this._material.uniforms.uSizeScale.value = (canvasPixelHeight / (2 * Math.tan(fovRadians / 2))) * pulse;

    const targets = rig.getTargetPositions();
    const uniformTargets = this._material.uniforms.beamTargets.value;
    for (let i = 0; i < HEAD_COUNT; i++) {
      uniformTargets[i].copy(targets[i]);
    }

    // Same reasoning as the dust layer: the crowd/dancefloor particles
    // use the artwork's real dominantColors, not the rig's boosted
    // vividColors - they read as picking up the room's ambient tone
    // rather than a second independent light source. colorA/colorB step
    // forward through every detected color in turn (not just the first
    // two) while the fragment shader's own mixT sine keeps blending
    // between whichever pair is currently active.
    const dominant = profile.artworkPalette && profile.artworkPalette.dominantColors;
    if (dominant && dominant.length >= 2) {
      const n = dominant.length;
      const raw = this._time * (1 / 33) * n;
      const i0 = Math.floor(raw) % n;
      const i1 = (i0 + 1) % n;
      this._material.uniforms.colorA.value.set(dominant[i0]);
      this._material.uniforms.colorB.value.set(dominant[i1]);
    } else {
      this._material.uniforms.colorA.value.copy(FALLBACK_A);
      this._material.uniforms.colorB.value.copy(FALLBACK_B);
    }
  }

  dispose(scene) {
    scene.remove(this._points);
    this._points.geometry.dispose();
    this._material.dispose();
  }
}
